import React, { useMemo, useState } from "react";
import imageCompression from "browser-image-compression";
import ImageTracer from "imagetracerjs";
import JSZip from "jszip";
import "./App.css";

function App() {
  const [previewUrl, setPreviewUrl] = useState("");
  const [exactSvgData, setExactSvgData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quality, setQuality] = useState("medium");

  const [bgTolerance, setBgTolerance] = useState(45);
  const [colorCount, setColorCount] = useState(8);
  const [lineOnly, setLineOnly] = useState(false);
  const [smoothEdges, setSmoothEdges] = useState(true);
  const [speckleSize, setSpeckleSize] = useState(8);
  const [lineSmoothness, setLineSmoothness] = useState(4);
  const [lineThickness, setLineThickness] = useState(2);
  const [blackWhiteOnly, setBlackWhiteOnly] = useState(false);
  const [cleanMode, setCleanMode] = useState(false);
  const [embroideryPrepMode, setEmbroideryPrepMode] = useState(false);

  const [lastPngUrl, setLastPngUrl] = useState("");
  const [lastEmbroiderySvg, setLastEmbroiderySvg] = useState("");
  const [lastProcessedMeta, setLastProcessedMeta] = useState(null);

  const qualityOptions = useMemo(
    () => ({
      high: {
        maxSizeMB: 2,
        maxWidthOrHeight: 2200,
        initialQuality: 0.92,
        useWebWorker: true,
      },
      medium: {
        maxSizeMB: 1,
        maxWidthOrHeight: 1500,
        initialQuality: 0.85,
        useWebWorker: true,
      },
      small: {
        maxSizeMB: 0.45,
        maxWidthOrHeight: 1000,
        initialQuality: 0.72,
        useWebWorker: true,
      },
    }),
    []
  );

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const loadImageFromDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

  const canvasToDataUrl = (canvas) => canvas.toDataURL("image/png");

  const dataUrlToBlob = async (dataUrl) => {
    const response = await fetch(dataUrl);
    return response.blob();
  };

  const removeEdgeBackgroundAndCrop = (img, tolerance = 40) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const { width, height } = imageData;

    const getPixel = (x, y) => {
      const index = (y * width + x) * 4;
      return [data[index], data[index + 1], data[index + 2], data[index + 3]];
    };

    const colorDistance = (pixel, base) => {
      const dr = pixel[0] - base[0];
      const dg = pixel[1] - base[1];
      const db = pixel[2] - base[2];
      return Math.sqrt(dr * dr + dg * dg + db * db);
    };

    const edgePixels = [
      getPixel(0, 0),
      getPixel(width - 1, 0),
      getPixel(0, height - 1),
      getPixel(width - 1, height - 1),
    ];

    const backgroundColor = edgePixels
      .reduce(
        (acc, pixel) => [acc[0] + pixel[0], acc[1] + pixel[1], acc[2] + pixel[2]],
        [0, 0, 0]
      )
      .map((v) => Math.round(v / edgePixels.length));

    const visited = new Uint8Array(width * height);
    const queue = [];

    const pushIfBackground = (x, y) => {
      if (x < 0 || y < 0 || x >= width || y >= height) return;
      const pixelIndex = y * width + x;
      if (visited[pixelIndex]) return;

      const pixel = getPixel(x, y);

      if (pixel[3] === 0) {
        visited[pixelIndex] = 1;
        return;
      }

      if (colorDistance(pixel, backgroundColor) <= tolerance) {
        visited[pixelIndex] = 1;
        queue.push([x, y]);
      }
    };

    for (let x = 0; x < width; x++) {
      pushIfBackground(x, 0);
      pushIfBackground(x, height - 1);
    }

    for (let y = 0; y < height; y++) {
      pushIfBackground(0, y);
      pushIfBackground(width - 1, y);
    }

    while (queue.length > 0) {
      const [x, y] = queue.shift();
      const i = (y * width + x) * 4;
      data[i + 3] = 0;

      pushIfBackground(x + 1, y);
      pushIfBackground(x - 1, y);
      pushIfBackground(x, y + 1);
      pushIfBackground(x, y - 1);
    }

    ctx.putImageData(imageData, 0, 0);

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return {
        canvas,
        dataUrl: canvasToDataUrl(canvas),
        width,
        height,
      };
    }

    const croppedWidth = maxX - minX + 1;
    const croppedHeight = maxY - minY + 1;
    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;

    croppedCtx.drawImage(
      canvas,
      minX,
      minY,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight
    );

    return {
      canvas: croppedCanvas,
      dataUrl: canvasToDataUrl(croppedCanvas),
      width: croppedWidth,
      height: croppedHeight,
    };
  };

  const smoothCanvas = (sourceCanvas) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const copy = new Uint8ClampedArray(data);

    const width = canvas.width;
    const height = canvas.height;

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        const alpha = copy[idx + 3];

        if (alpha === 0) {
          let opaqueNeighbors = 0;
          let r = 0;
          let g = 0;
          let b = 0;

          for (let ny = -1; ny <= 1; ny++) {
            for (let nx = -1; nx <= 1; nx++) {
              if (nx === 0 && ny === 0) continue;
              const ni = ((y + ny) * width + (x + nx)) * 4;
              if (copy[ni + 3] > 0) {
                opaqueNeighbors++;
                r += copy[ni];
                g += copy[ni + 1];
                b += copy[ni + 2];
              }
            }
          }

          if (opaqueNeighbors >= 7) {
            data[idx] = Math.round(r / opaqueNeighbors);
            data[idx + 1] = Math.round(g / opaqueNeighbors);
            data[idx + 2] = Math.round(b / opaqueNeighbors);
            data[idx + 3] = 255;
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const blurCanvas = (sourceCanvas, radius = 1.2) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.filter = `blur(${radius}px)`;
    ctx.drawImage(sourceCanvas, 0, 0);
    ctx.filter = "none";
    return canvas;
  };

  const posterizeCanvas = (sourceCanvas, levelsPerChannel = 4) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const levels = clamp(levelsPerChannel, 2, 16);
    const step = 255 / (levels - 1);

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      data[i] = Math.round(Math.round(data[i] / step) * step);
      data[i + 1] = Math.round(Math.round(data[i + 1] / step) * step);
      data[i + 2] = Math.round(Math.round(data[i + 2] / step) * step);
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const blackWhiteCanvas = (sourceCanvas, threshold = 185) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;

      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

      const value = gray < threshold ? 0 : 255;
      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
      data[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const linePreservingBW = (sourceCanvas, threshold = 175) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;

      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const value = gray < threshold ? 0 : 255;

      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
      data[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const cleanIsolatedPixels = (sourceCanvas) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const copy = new Uint8ClampedArray(data);
    const { width, height } = canvas;

    const getBrightness = (arr, x, y) => {
      const i = (y * width + x) * 4;
      if (arr[i + 3] === 0) return 255;
      return (arr[i] + arr[i + 1] + arr[i + 2]) / 3;
    };

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const i = (y * width + x) * 4;
        if (copy[i + 3] === 0) continue;

        const center = getBrightness(copy, x, y);
        let blackNeighbors = 0;
        let whiteNeighbors = 0;

        for (let ny = -1; ny <= 1; ny++) {
          for (let nx = -1; nx <= 1; nx++) {
            if (nx === 0 && ny === 0) continue;
            const b = getBrightness(copy, x + nx, y + ny);
            if (b < 128) blackNeighbors++;
            else whiteNeighbors++;
          }
        }

        if (center >= 128 && blackNeighbors >= 7) {
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 255;
        }

        if (center < 128 && whiteNeighbors >= 7) {
          data[i] = 255;
          data[i + 1] = 255;
          data[i + 2] = 255;
          data[i + 3] = 255;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const removeSmallBlackIslands = (sourceCanvas, maxIslandSize = 80) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const { width, height } = canvas;

    const visited = new Uint8Array(width * height);

    const isBlack = (x, y) => {
      const i = (y * width + x) * 4;
      if (data[i + 3] === 0) return false;
      const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      return brightness < 128;
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const start = y * width + x;
        if (visited[start]) continue;
        visited[start] = 1;

        if (!isBlack(x, y)) continue;

        const queue = [[x, y]];
        const cluster = [[x, y]];
        let touchesBorder = x === 0 || y === 0 || x === width - 1 || y === height - 1;

        while (queue.length) {
          const [cx, cy] = queue.shift();

          const neighbors = [
            [cx + 1, cy],
            [cx - 1, cy],
            [cx, cy + 1],
            [cx, cy - 1],
          ];

          for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;

            const np = ny * width + nx;
            if (visited[np]) continue;
            visited[np] = 1;

            if (isBlack(nx, ny)) {
              if (nx === 0 || ny === 0 || nx === width - 1 || ny === height - 1) {
                touchesBorder = true;
              }
              queue.push([nx, ny]);
              cluster.push([nx, ny]);
            }
          }
        }

        if (!touchesBorder && cluster.length <= maxIslandSize) {
          for (const [ix, iy] of cluster) {
            const i = (iy * width + ix) * 4;
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
            data[i + 3] = 255;
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const erodeBlackPixels = (sourceCanvas, iterations = 1) => {
    let currentCanvas = sourceCanvas;

    for (let pass = 0; pass < iterations; pass++) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      canvas.width = currentCanvas.width;
      canvas.height = currentCanvas.height;
      ctx.drawImage(currentCanvas, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const copy = new Uint8ClampedArray(data);
      const { width, height } = canvas;

      const isBlack = (arr, x, y) => {
        const i = (y * width + x) * 4;
        if (arr[i + 3] === 0) return false;
        return (arr[i] + arr[i + 1] + arr[i + 2]) / 3 < 128;
      };

      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const i = (y * width + x) * 4;
          if (!isBlack(copy, x, y)) continue;

          let blackNeighbors = 0;
          for (let ny = -1; ny <= 1; ny++) {
            for (let nx = -1; nx <= 1; nx++) {
              if (isBlack(copy, x + nx, y + ny)) blackNeighbors++;
            }
          }

          if (blackNeighbors <= 4) {
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
            data[i + 3] = 255;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      currentCanvas = canvas;
    }

    return currentCanvas;
  };

  const smoothJaggedEdges = (sourceCanvas) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const copy = new Uint8ClampedArray(data);
    const { width, height } = canvas;

    const brightnessAt = (arr, x, y) => {
      const i = (y * width + x) * 4;
      if (arr[i + 3] === 0) return 255;
      return (arr[i] + arr[i + 1] + arr[i + 2]) / 3;
    };

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const i = (y * width + x) * 4;
        const center = brightnessAt(copy, x, y);

        let blackNeighbors = 0;
        let whiteNeighbors = 0;

        for (let ny = -1; ny <= 1; ny++) {
          for (let nx = -1; nx <= 1; nx++) {
            if (nx === 0 && ny === 0) continue;
            const b = brightnessAt(copy, x + nx, y + ny);
            if (b < 128) blackNeighbors++;
            else whiteNeighbors++;
          }
        }

        if (center >= 128 && blackNeighbors >= 6) {
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 255;
        } else if (center < 128 && whiteNeighbors >= 6) {
          data[i] = 255;
          data[i + 1] = 255;
          data[i + 2] = 255;
          data[i + 3] = 255;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const thickenLinesCanvas = (sourceCanvas, strength = 1) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;

    const passes = clamp(Math.round(strength), 1, 8);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let y = -passes; y <= passes; y++) {
      for (let x = -passes; x <= passes; x++) {
        if (Math.sqrt(x * x + y * y) <= passes) {
          ctx.drawImage(sourceCanvas, x, y);
        }
      }
    }

    return canvas;
  };

  const removeSmallSpeckles = (sourceCanvas, minClusterSize = 8) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    ctx.drawImage(sourceCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const { width, height } = canvas;

    const visited = new Uint8Array(width * height);
    const alphaAt = (x, y) => data[(y * width + x) * 4 + 3];

    const isForeground = (x, y) => {
      const i = (y * width + x) * 4;
      if (data[i + 3] === 0) return false;
      const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
      return brightness < 245;
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const p = y * width + x;
        if (visited[p]) continue;
        visited[p] = 1;

        if (!isForeground(x, y) || alphaAt(x, y) === 0) continue;

        const queue = [[x, y]];
        const cluster = [[x, y]];

        while (queue.length) {
          const [cx, cy] = queue.shift();

          const neighbors = [
            [cx + 1, cy],
            [cx - 1, cy],
            [cx, cy + 1],
            [cx, cy - 1],
          ];

          for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const np = ny * width + nx;
            if (visited[np]) continue;
            visited[np] = 1;

            if (isForeground(nx, ny) && alphaAt(nx, ny) > 0) {
              queue.push([nx, ny]);
              cluster.push([nx, ny]);
            }
          }
        }

        if (cluster.length < minClusterSize) {
          for (const [sx, sy] of cluster) {
            const i = (sy * width + sx) * 4;
            data[i + 3] = 0;
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const applyCleanPreset = () => {
    setQuality("high");
    setBgTolerance(28);
    setColorCount(2);
    setLineOnly(true);
    setSmoothEdges(true);
    setSpeckleSize(4);
    setLineSmoothness(7);
    setLineThickness(1);
    setBlackWhiteOnly(false);
    setCleanMode(false);
    setEmbroideryPrepMode(true);
  };

  const buildEmbroideryPrepSvg = (svgText, strokeWidth = 1) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svg = doc.documentElement;

      svg.setAttribute("fill-rule", "evenodd");
      svg.setAttribute("shape-rendering", "auto");

      const paths = svg.querySelectorAll("path");
      paths.forEach((path) => {
        path.setAttribute("fill", "black");
        path.setAttribute("stroke", "none");
        path.removeAttribute("stroke-width");
        path.removeAttribute("stroke-linejoin");
        path.removeAttribute("stroke-linecap");
      });

      return new XMLSerializer().serializeToString(doc);
    } catch {
      return svgText;
    }
  };

  const preprocessCanvas = async (img) => {
    const removed = removeEdgeBackgroundAndCrop(img, bgTolerance);
    let workingCanvas = removed.canvas;

    const effectiveEmbroidery = embroideryPrepMode;
    const effectiveSmoothEdges = effectiveEmbroidery ? true : cleanMode ? true : smoothEdges;
    const effectiveLineOnly = effectiveEmbroidery ? true : cleanMode ? true : lineOnly;
    const effectiveBlackWhite = cleanMode ? true : blackWhiteOnly;
    const effectiveSpeckle = effectiveEmbroidery
      ? Math.min(Math.max(speckleSize, 3), 6)
      : cleanMode
      ? Math.min(speckleSize, 5)
      : speckleSize;
    const effectiveThickness = effectiveEmbroidery
      ? 1
      : cleanMode
      ? Math.max(lineThickness, 2)
      : lineThickness;
    const effectiveSmoothness = effectiveEmbroidery
      ? Math.max(lineSmoothness, 6)
      : lineSmoothness;

    if (effectiveSmoothEdges) {
      workingCanvas = smoothCanvas(workingCanvas);
      workingCanvas = blurCanvas(workingCanvas, 0.35);
    }

    if (effectiveLineOnly) {
      workingCanvas = blurCanvas(workingCanvas, effectiveEmbroidery ? 0.18 : 0.5);
      workingCanvas = linePreservingBW(workingCanvas, effectiveEmbroidery ? 158 : 180);
      workingCanvas = cleanIsolatedPixels(workingCanvas);
      workingCanvas = cleanIsolatedPixels(workingCanvas);
      workingCanvas = removeSmallBlackIslands(workingCanvas, effectiveEmbroidery ? 180 : 90);
      workingCanvas = erodeBlackPixels(workingCanvas, effectiveEmbroidery ? 1 : 0);
      workingCanvas = smoothJaggedEdges(workingCanvas);
      workingCanvas = removeSmallBlackIslands(workingCanvas, effectiveEmbroidery ? 140 : 70);

      if (!effectiveEmbroidery && effectiveThickness > 1) {
        workingCanvas = thickenLinesCanvas(workingCanvas, effectiveThickness - 1);
      }
    } else {
      if (effectiveBlackWhite) {
        workingCanvas = blackWhiteCanvas(workingCanvas, 185);
        workingCanvas = thickenLinesCanvas(workingCanvas, effectiveThickness);
      } else {
        const levels = colorCount <= 4 ? 3 : colorCount <= 8 ? 4 : colorCount <= 12 ? 5 : 6;
        workingCanvas = posterizeCanvas(workingCanvas, levels);
      }

      workingCanvas = removeSmallSpeckles(workingCanvas, effectiveSpeckle);
    }

    return {
      canvas: workingCanvas,
      dataUrl: canvasToDataUrl(workingCanvas),
      width: workingCanvas.width,
      height: workingCanvas.height,
      meta: {
        embroideryPrepMode: effectiveEmbroidery,
        lineOnly: effectiveLineOnly,
        blackWhiteOnly: effectiveBlackWhite,
        lineThickness: effectiveThickness,
        lineSmoothness: effectiveSmoothness,
        cleanupLevel: effectiveSpeckle,
        backgroundTolerance: bgTolerance,
        quality,
      },
    };
  };

  const simplifySvgPaths = (svgText) => {
    let svg = svgText;
    svg = svg.replace(/\s{2,}/g, " ");
    svg = svg.replace(/,\s+/g, ",");
    svg = svg.replace(/\s+([MLCQZmlcqz])/g, " $1");
    svg = svg.replace(/\s*stroke="[^"]*"/g, "");
    svg = svg.replace(/\s*stroke-width="[^"]*"/g, "");
    return svg;
  };

  const applyUnifiedStrokeSvg = (svgText, strokeWidth = 1) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svg = doc.documentElement;

      svg.setAttribute("fill-rule", "evenodd");

      const paths = svg.querySelectorAll("path");
      paths.forEach((path) => {
        path.setAttribute("fill", "none");
        path.setAttribute("stroke", "black");
        path.setAttribute("stroke-width", String(strokeWidth));
        path.setAttribute("stroke-linejoin", "round");
        path.setAttribute("stroke-linecap", "round");
      });

      return new XMLSerializer().serializeToString(doc);
    } catch {
      return svgText;
    }
  };

  const traceToSvg = (dataUrl) =>
    new Promise((resolve, reject) => {
      try {
        const effectiveLineOnly = embroideryPrepMode ? true : cleanMode ? true : lineOnly;
        const effectiveBlackWhite = cleanMode ? true : blackWhiteOnly;
        const effectiveThickness = embroideryPrepMode ? 1 : lineThickness;
        const colorMode = effectiveLineOnly || effectiveBlackWhite ? 2 : colorCount;

        ImageTracer.imageToSVG(
          dataUrl,
          (svgString) => {
            if (!svgString || typeof svgString !== "string") {
              reject(new Error("SVG tracing failed."));
              return;
            }

            let processedSvg = simplifySvgPaths(svgString);

            if (!embroideryPrepMode && (effectiveLineOnly || effectiveBlackWhite)) {
              processedSvg = applyUnifiedStrokeSvg(processedSvg, effectiveThickness);
            }

            if (embroideryPrepMode) {
              processedSvg = buildEmbroideryPrepSvg(processedSvg, effectiveThickness);
            }

            resolve(processedSvg);
          },
          {
            numberofcolors: colorMode,
            colorquantcycles: 2,
            ltres: embroideryPrepMode
              ? quality === "high"
                ? 2.2
                : quality === "medium"
                ? 2.8
                : 3.4
              : quality === "high"
              ? 1.2
              : quality === "medium"
              ? 1.8
              : 2.4,
            qtres: embroideryPrepMode
              ? quality === "high"
                ? 2.2
                : quality === "medium"
                ? 2.8
                : 3.4
              : quality === "high"
              ? 1.2
              : quality === "medium"
              ? 1.8
              : 2.4,
            pathomit: embroideryPrepMode
              ? Math.max(2, speckleSize)
              : Math.max(1, speckleSize - 1),
            roundcoords: embroideryPrepMode ? 2 : 1,
            scale: 1,
            strokewidth: 0,
            linefilter: false,
            rightangleenhance: false,
            desc: false,
            viewbox: true,
          }
        );
      } catch (err) {
        reject(err);
      }
    });

  const handleFile = async (file) => {
    if (!file) return;

    setLoading(true);
    setError("");
    setPreviewUrl("");
    setExactSvgData("");
    setLastPngUrl("");
    setLastEmbroiderySvg("");
    setLastProcessedMeta(null);

    try {
      const compressionOptions = qualityOptions[quality] || qualityOptions.medium;
      let optimizedFile = file;

      try {
        optimizedFile = await imageCompression(file, compressionOptions);
      } catch (compressionError) {
        console.warn("Compression failed, using original file.", compressionError);
      }

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const img = await loadImageFromDataUrl(e.target.result);
          const processed = await preprocessCanvas(img);

          setPreviewUrl(processed.dataUrl);
          setLastPngUrl(processed.dataUrl);
          setLastProcessedMeta(processed.meta);

          const svgString = await traceToSvg(processed.dataUrl);
          setExactSvgData(svgString);

          const embroiderySvg = buildEmbroideryPrepSvg(
            svgString,
            embroideryPrepMode ? 1 : Math.max(lineThickness, 1)
          );
          setLastEmbroiderySvg(embroiderySvg);
        } catch (err) {
          console.error(err);
          setError("Image processing failed.");
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError("Could not read file.");
        setLoading(false);
      };

      reader.readAsDataURL(optimizedFile);
    } catch (err) {
      console.error(err);
      setError("Something went wrong.");
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const downloadBlob = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSVG = () => {
    if (!exactSvgData) return;
    downloadBlob(exactSvgData, "image/svg+xml", "haida-evolved.svg");
  };

  const downloadEmbroideryPrepSvg = () => {
    if (!lastEmbroiderySvg) return;
    downloadBlob(lastEmbroiderySvg, "image/svg+xml", "haida-evolved-embroidery-prep.svg");
  };

  const downloadSettingsJson = () => {
    if (!lastProcessedMeta) return;
    downloadBlob(
      JSON.stringify(lastProcessedMeta, null, 2),
      "application/json",
      "haida-evolved-settings.json"
    );
  };

  const downloadPNG = async () => {
    if (!lastPngUrl) return;
    const response = await fetch(lastPngUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "haida-evolved-cleaned.png";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadEmbroideryBundle = async () => {
    if (!lastPngUrl || !lastEmbroiderySvg || !lastProcessedMeta) return;

    try {
      const zip = new JSZip();
      const pngBlob = await dataUrlToBlob(lastPngUrl);

      zip.file("haida-evolved-cleaned.png", pngBlob);
      zip.file("haida-evolved-embroidery-prep.svg", lastEmbroiderySvg);
      zip.file("haida-evolved-settings.json", JSON.stringify(lastProcessedMeta, null, 2));
      zip.file(
        "README.txt",
        [
          "Embroidery Prep Bundle",
          "",
          "This bundle does not contain a VP3 embroidery stitch file.",
          "Use the embroidery-prep SVG or cleaned PNG in Ink/Stitch, mySewnet, or other digitizing software.",
          "",
          "Suggested next step:",
          "1. Open the SVG in digitizing software",
          "2. Assign stitch types",
          "3. Adjust density/underlay",
          "4. Export VP3 from that software",
        ].join("\n")
      );

      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "haida-evolved-embroidery-bundle.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("Could not create embroidery bundle.");
    }
  };

  const copySVG = async () => {
    if (!exactSvgData) return;
    try {
      await navigator.clipboard.writeText(exactSvgData);
      alert("SVG copied to clipboard.");
    } catch (err) {
      console.error(err);
      alert("Could not copy SVG.");
    }
  };

  return (
    <div className="app">
      <h1>🪶 Haida Evolved</h1>
      <p className="subtitle">Convert designs into cleaner SVG for embroidery and art workflows</p>

      <div className="controls-panel">
        <div className="quality-row">
          <label htmlFor="quality">Output quality</label>
          <select id="quality" value={quality} onChange={(e) => setQuality(e.target.value)}>
            <option value="high">High detail</option>
            <option value="medium">Medium</option>
            <option value="small">Small / simpler</option>
          </select>
        </div>

        <div className="slider-group">
          <label htmlFor="bgTolerance">
            Background tolerance: <strong>{bgTolerance}</strong>
          </label>
          <input
            id="bgTolerance"
            type="range"
            min="5"
            max="100"
            step="1"
            value={bgTolerance}
            onChange={(e) => setBgTolerance(Number(e.target.value))}
          />
        </div>

        <div className="slider-group">
          <label htmlFor="colorCount">
            Color count: <strong>{colorCount}</strong>
          </label>
          <input
            id="colorCount"
            type="range"
            min="2"
            max="16"
            step="1"
            value={colorCount}
            onChange={(e) => setColorCount(Number(e.target.value))}
            disabled={lineOnly || blackWhiteOnly || cleanMode || embroideryPrepMode}
          />
        </div>

        <div className="slider-group">
          <label htmlFor="speckleSize">
            Cleanup level: <strong>{speckleSize}</strong>
          </label>
          <input
            id="speckleSize"
            type="range"
            min="1"
            max="30"
            step="1"
            value={speckleSize}
            onChange={(e) => setSpeckleSize(Number(e.target.value))}
          />
        </div>

        <div className="slider-group">
          <label htmlFor="lineSmoothness">
            Line smoothness: <strong>{lineSmoothness}</strong>
          </label>
          <input
            id="lineSmoothness"
            type="range"
            min="1"
            max="10"
            step="1"
            value={lineSmoothness}
            onChange={(e) => setLineSmoothness(Number(e.target.value))}
          />
        </div>

        <div className="slider-group">
          <label htmlFor="lineThickness">
            Line thickness: <strong>{lineThickness}</strong>
          </label>
          <input
            id="lineThickness"
            type="range"
            min="1"
            max="8"
            step="1"
            value={lineThickness}
            onChange={(e) => setLineThickness(Number(e.target.value))}
          />
        </div>

        <div className="toggle-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={smoothEdges}
              onChange={(e) => setSmoothEdges(e.target.checked)}
            />
            Smooth edges
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={lineOnly}
              onChange={(e) => setLineOnly(e.target.checked)}
            />
            Line-only mode
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={blackWhiteOnly}
              onChange={(e) => setBlackWhiteOnly(e.target.checked)}
            />
            Black/white only
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={cleanMode}
              onChange={(e) => setCleanMode(e.target.checked)}
            />
            Clean mode
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={embroideryPrepMode}
              onChange={(e) => setEmbroideryPrepMode(e.target.checked)}
            />
            Embroidery prep mode
          </label>
        </div>

        <div className="button-row preset-row">
          <button type="button" onClick={applyCleanPreset}>
            ✨ One-Click Clean Mode
          </button>
        </div>
      </div>

      <div className="drop-zone" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
        <p>Drag & Drop Image</p>
        <p className="drop-subtext">or choose a file below</p>
        <input type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>

      {loading && <p className="status">Converting...</p>}
      {error && <p className="error-label">⚠ {error}</p>}

      {previewUrl && (
        <>
          <div className="preview-section">
            <div className="preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          </div>

          <p className="ready-label">✅ Export ready</p>

          <div className="button-row">
            <button onClick={downloadSVG}>⬇ Download SVG</button>
            <button onClick={downloadPNG}>⬇ Download Clean PNG</button>
            <button onClick={downloadEmbroideryPrepSvg}>⬇ Download Embroidery Prep SVG</button>
            <button onClick={downloadSettingsJson}>⬇ Download Settings JSON</button>
            <button onClick={downloadEmbroideryBundle}>⬇ Download Embroidery Bundle ZIP</button>
            <button onClick={copySVG}>⧉ Copy SVG Code</button>
          </div>

          {exactSvgData && (
            <div className="svg-code-box">
              <h3>SVG Code Preview</h3>
              <textarea readOnly value={exactSvgData} rows={12} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
